// --- Dependencies -------------------------------------------------------------------------------
import { useEffect, useState } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import constants from "@/config/constants";
import useFetch from "@/hooks/useFetch";
import usePopup from "@/hooks/usePopup";
import useTranslation from "@/hooks/useTranslation";
import type { TyLangValue, TyLatLng, TyProjectRef, TyUnitFlags } from "@/types";
import Icon from "@/UI/atoms/Icon";
import FieldsGroup from "@/UI/components/FieldsGroup";
import ChoiceInput from "@/UI/inputs/ChoiceInput";
import MapInput from "@/UI/inputs/MapInput";
import MultiLangInput from "@/UI/inputs/MultiLangInput";
import MultiSelectInput from "@/UI/inputs/MultiSelectInput";
import NumberInput from "@/UI/inputs/NumberInput";
import SelectInput from "@/UI/inputs/SelectInput";
import TextInput from "@/UI/inputs/TextInput";
import RenamePopup from "@/UI/popups/RenamePopup";
import UnitFieldsPopup, { type TyUnitFieldValues } from "@/UI/popups/UnitFieldsPopup";
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------
export type TyBulkUnitsDetails = { project: TyProjectRef; onDone: () => void };

type Props = { onClose: () => void; details?: unknown };

type TyNumbers = Record<string, number | null>;

/** Starts every count and measure at "nobody said", which is not the same as zero. */
const emptyGroup = (keys: readonly string[]): TyNumbers => Object.fromEntries(keys.map((key) => [key, null]));
const emptyFlags = (): TyUnitFlags => Object.fromEntries(constants.unitGroups.flags.map((key) => [key, null])) as TyUnitFlags;

/**
 * Lines, commas, and `301-312` ranges, read out of one typed value. A range only expands when both
 * ends share a prefix and end in digits, so "A1-A8" works and "ground-floor" is left alone as the
 * one number somebody meant.
 */
function readNumbers(raw: string): string[] {
    const pieces = raw
        .split(/[\n,،]/)
        .map((piece) => piece.trim())
        .filter(Boolean);
    const out: string[] = [];

    for (const piece of pieces) {
        const range = piece.match(/^(.*?)(\d+)\s*[-–]\s*(.*?)(\d+)$/);
        if (range && range[1] === range[3]) {
            const [, prefix, from, , to] = range;
            const start = Number(from);
            const end = Number(to);
            // Guarded: a reversed or enormous range is a typo, and the cap is the backend's too.
            if (end >= start && end - start < constants.unitBulkMax) {
                for (let current = start; current <= end; current += 1) out.push(`${prefix}${String(current).padStart(from.length, "0")}`);
                continue;
            }
        }
        out.push(piece);
    }

    return out;
}

/**
 * A floor at a time. Nobody creates twelve flats through twelve dialogs.
 *
 * **It asks everything the single form asks except what cannot be shared** — the number, which is the
 * list, and the deed number, which is one piece of paper per unit. Everything else about a floor of
 * flats is genuinely the same: the block, the floor, the type, the price, the areas, the rooms, the
 * features, the air conditioning, the facing, whether it shows on the catalog. Creating twelve units
 * and then editing twelve of them was the old answer, and it was the reason this form existed.
 *
 * **The numbers are composed here**, not on the server: each one is added to a list, and a typed
 * range or a pasted row explodes into that list as it is added. The API takes a plain list and never
 * has to learn a numbering grammar it would then have to argue about.
 */
export default function BulkUnitsPopup({ onClose, details }: Props) {
    const t = useTranslation();
    const { project, onDone } = details as TyBulkUnitsDetails;
    const addComp = usePopup((state) => state.addComp);

    const [draft, setDraft] = useState("");
    const [numbers, setNumbers] = useState<string[]>([]);
    const [building, setBuilding] = useState("");
    /** Local, because a block can be created from this form and must appear in the list at once. */
    const [buildings, setBuildings] = useState(project.buildings);
    const [floor, setFloor] = useState<number | null>(null);
    const [type, setType] = useState("");
    const [usage, setUsage] = useState("");
    const [offer, setOffer] = useState("");
    const [price, setPrice] = useState<number | null>(null);
    const [rentPeriod, setRentPeriod] = useState<number | null>(null);
    const [description, setDescription] = useState<TyLangValue[]>([]);
    const [measures, setMeasures] = useState<TyNumbers>(emptyGroup(constants.unitGroups.measures));
    const [counts, setCounts] = useState<TyNumbers>(emptyGroup(constants.unitGroups.counts));
    const [flags, setFlags] = useState<TyUnitFlags>(emptyFlags());
    const [acSystem, setAcSystem] = useState("");
    const [facades, setFacades] = useState<string[]>([]);
    const [location, setLocation] = useState<TyLatLng | null>(project.location ?? null);
    const [useProjectGallery, setUseProjectGallery] = useState(true);
    const [isPublic, setIsPublic] = useState(false);
    const [notes, setNotes] = useState("");
    const [formError, setFormError] = useState<string | null>(null);

    const { loading, error, succeededAt, invoke } = useFetch("/properties/units/bulk", { method: "POST" }, false);

    useEffect(() => {
        if (!succeededAt) return;
        onClose();
        onDone();
    }, [succeededAt]); // eslint-disable-line react-hooks/exhaustive-deps

    /** Adds whatever is typed — one number, a range, or a pasted row — and clears the box for the next. */
    const addNumbers = () => {
        const added = readNumbers(draft);
        if (!added.length) return;
        setNumbers((current) => [...new Set([...current, ...added])]);
        setDraft("");
    };

    const handleSubmit = () => {
        setFormError(null);
        if (!numbers.length) return setFormError(t.properties.numbersRequired);

        invoke({
            method: "POST",
            data: {
                project: project._id,
                building: building || null,
                numbers,
                /** Everything they have in common. The API spreads it across every row it writes. */
                template: {
                    floor,
                    type: type || null,
                    usage: usage || null,
                    offer: offer || null,
                    price,
                    rentPeriod,
                    description,
                    measures,
                    counts,
                    flags,
                    choices: { acSystem: acSystem || null, facades },
                    location,
                    useProjectGallery,
                    isPublic,
                    notes: notes.trim() || null,
                },
            },
        });
    };

    /** Add a block without leaving the form — the same affordance the single unit form carries. */
    const openAddBuilding = () =>
        addComp({
            name: "add-building",
            Component: RenamePopup,
            anim: "scale",
            details: {
                title: t.properties.addBuilding,
                value: "",
                path: `/properties/projects/${project._id}/buildings`,
                method: "POST",
                onDone: (data: unknown) => {
                    const next = (data as { buildings?: typeof buildings } | undefined)?.buildings;
                    if (!next?.length) return;
                    setBuildings(next);
                    const created = next.find((entry) => !buildings.some((old) => old._id === entry._id));
                    if (created) setBuilding(created._id);
                },
            },
        });

    const openFields = (kind: "counts" | "flags") =>
        addComp({
            name: `unit-${kind}`,
            Component: UnitFieldsPopup,
            anim: "scale",
            details: {
                title: kind === "counts" ? t.properties.groups.counts : t.properties.groups.flags,
                keys: constants.unitGroups[kind],
                labels: kind === "counts" ? t.properties.counts : t.properties.flags,
                kind,
                values: kind === "counts" ? counts : flags,
                onConfirm: (values: TyUnitFieldValues) => (kind === "counts" ? setCounts(values as TyNumbers) : setFlags(values as TyUnitFlags)),
            },
        });

    return (
        <section className="side-form">
            <header className="side-form__header">
                <h2 className="side-form__title">{t.properties.bulkTitle}</h2>
                <button type="button" className="side-form__close" onClick={onClose} aria-label={t.common.close}>
                    <Icon name="close" size={16} />
                </button>
            </header>

            <div className="side-form__body">
                <p className="side-form__hint">{t.properties.bulkHint}</p>
                <p className="side-form__hint">{t.properties.fields.numbersHint}</p>

                {/* One at a time, or a whole range at once. Enter adds, because a person typing a
                    column of numbers never reaches for the mouse between them. */}
                <div className="side-form__with-add side-form__with-add--joined">
                    <TextInput value={draft} onChange={setDraft} onEnter={addNumbers} label={t.properties.fields.numbers} disabled={loading} />
                    <button
                        type="button"
                        className="side-form__add-beside"
                        onClick={addNumbers}
                        disabled={loading || !draft.trim()}
                        aria-label={t.common.add}
                        title={t.common.add}
                    >
                        <Icon name="add" size={16} />
                    </button>
                </div>

                {/* What is actually going to be created, before it is — one row that scrolls, so a
                    hundred numbers cannot push the rest of the form off the screen. */}
                {numbers.length > 0 && (
                    <div className="bulk-units__added">
                        <span className="bulk-units__count">{t.properties.bulkPreview.replace("{count}", String(numbers.length))}</span>

                        <div className="bulk-units__rail">
                            {numbers.map((item) => (
                                <span key={item} className="bulk-units__chip">
                                    <span>{item}</span>
                                    <button
                                        type="button"
                                        className="bulk-units__drop"
                                        onClick={() => setNumbers((current) => current.filter((entry) => entry !== item))}
                                        disabled={loading}
                                        aria-label={t.common.remove}
                                    >
                                        <Icon name="close" size={12} />
                                    </button>
                                </span>
                            ))}
                        </div>
                    </div>
                )}

                <div className="side-form__row side-form__row--pair">
                    <div className="side-form__with-add side-form__with-add--joined">
                        <SelectInput
                            value={building}
                            onChange={setBuilding}
                            options={buildings.map((entry) => ({ value: entry._id, label: entry.name }))}
                            label={t.properties.fields.building}
                            placeholder={buildings.length ? t.properties.notSet : t.properties.emptyBuildingsShort}
                            disabled={loading || !buildings.length}
                        />
                        <button
                            type="button"
                            className="side-form__add-beside"
                            onClick={openAddBuilding}
                            disabled={loading}
                            aria-label={t.properties.addBuilding}
                            title={t.properties.addBuilding}
                        >
                            <Icon name="add" size={16} />
                        </button>
                    </div>

                    <NumberInput value={floor} onChange={setFloor} label={t.properties.fields.floor} disabled={loading} />
                </div>

                <div className="side-form__row side-form__row--pair">
                    <SelectInput
                        value={type}
                        onChange={setType}
                        options={constants.unitTypes.map((item) => ({ value: item, label: t.properties.unitTypes[item] }))}
                        label={t.properties.fields.type}
                        placeholder={t.properties.notSet}
                        disabled={loading}
                    />
                    <ChoiceInput
                        value={usage}
                        onChange={setUsage}
                        options={constants.unitUsages.map((item) => ({ value: item, label: t.properties.usages[item] }))}
                        label={t.properties.fields.usage}
                        disabled={loading}
                    />
                </div>

                <div className="side-form__row">
                    <ChoiceInput
                        value={offer}
                        onChange={setOffer}
                        options={constants.unitOffers.map((item) => ({ value: item, label: t.properties.offers[item] }))}
                        label={t.properties.fields.offer}
                        disabled={loading}
                    />
                    <NumberInput
                        value={price}
                        onChange={setPrice}
                        label={t.properties.fields.price}
                        suffix={t.properties.currency}
                        disabled={loading}
                    />
                    {/* Only under rent — a sale price covers no period, and the API clears it anyway. */}
                    {offer === "rent" && (
                        <NumberInput
                            value={rentPeriod}
                            onChange={setRentPeriod}
                            label={t.properties.fields.rentPeriod}
                            placeholder={t.properties.fields.rentPeriodHint}
                            suffix={t.properties.months}
                            min={1}
                            disabled={loading}
                        />
                    )}
                </div>

                {/* --- The groups, each drawn from the constants, never by hand ------------------ */}
                <div className="side-form__group">
                    <span className="side-form__group-title">{t.properties.groups.measures}</span>
                    <div className="side-form__grid">
                        {constants.unitGroups.measures.map((key) => (
                            <NumberInput
                                key={key}
                                value={measures[key] ?? null}
                                onChange={(value) => setMeasures((current) => ({ ...current, [key]: value }))}
                                label={t.properties.measures[key]}
                                suffix={t.properties.sqm}
                                disabled={loading}
                            />
                        ))}
                    </div>
                </div>

                <FieldsGroup
                    title={t.properties.groups.counts}
                    entries={constants.unitGroups.counts
                        .filter((key) => counts[key] !== null)
                        .map((key) => ({ key, label: t.properties.counts[key], count: counts[key] as number }))}
                    onOpen={() => openFields("counts")}
                    disabled={loading}
                />

                <FieldsGroup
                    title={t.properties.groups.flags}
                    entries={constants.unitGroups.flags
                        .filter((key) => flags[key] !== null)
                        .map((key) => ({ key, label: t.properties.flags[key], on: !!flags[key] }))}
                    onOpen={() => openFields("flags")}
                    disabled={loading}
                />

                <div className="side-form__group">
                    <span className="side-form__group-title">{t.properties.groups.choices}</span>

                    <div className="side-form__row side-form__row--split">
                        <MultiSelectInput
                            value={facades}
                            onChange={setFacades}
                            options={constants.unitFacades.map((item) => ({ value: item, label: t.properties.facades[item] }))}
                            label={t.properties.choices.facades}
                            disabled={loading}
                        />
                        <SelectInput
                            value={acSystem}
                            onChange={setAcSystem}
                            options={constants.acSystems.map((item) => ({ value: item, label: t.properties.acSystems[item] }))}
                            label={t.properties.choices.acSystem}
                            placeholder={t.properties.notSet}
                            disabled={loading}
                        />
                    </div>
                </div>

                <MultiLangInput value={description} onChange={setDescription} label={t.properties.fields.description} multiline disabled={loading} />

                <MapInput value={location} onChange={setLocation} label={t.properties.fields.location} disabled={loading} />

                {/* No file picker here, unlike the single form: one set of photographs spread across
                    twelve units is twelve copies of the same picture in the drive. */}
                <label className={`side-form__toggle${useProjectGallery ? " side-form__toggle--on" : ""}`}>
                    <input
                        type="checkbox"
                        checked={useProjectGallery}
                        onChange={(event) => setUseProjectGallery(event.target.checked)}
                        disabled={loading}
                    />
                    <span className="side-form__toggle-icon">
                        <Icon name="image" size={18} />
                    </span>
                    <span className="side-form__toggle-text">{t.properties.fields.useProjectGallery}</span>
                    <span className="side-form__toggle-mark" aria-hidden="true">
                        <Icon name="check" size={12} />
                    </span>
                </label>

                <label className={`side-form__toggle${isPublic ? " side-form__toggle--on" : ""}`}>
                    <input type="checkbox" checked={isPublic} onChange={(event) => setIsPublic(event.target.checked)} disabled={loading} />
                    <span className="side-form__toggle-icon">
                        <Icon name="eye" size={18} />
                    </span>
                    <span className="side-form__toggle-text">{t.properties.fields.showInCatalog}</span>
                    <span className="side-form__toggle-mark" aria-hidden="true">
                        <Icon name="check" size={12} />
                    </span>
                </label>

                <TextInput value={notes} onChange={setNotes} label={t.properties.fields.notes} multiline disabled={loading} />

                {(formError || error) && <p className="side-form__error">{formError ?? error}</p>}
            </div>

            <footer className="side-form__footer">
                <button type="button" className="side-form__cancel" onClick={onClose} disabled={loading}>
                    {t.common.cancel}
                </button>
                <button type="button" className="side-form__submit" onClick={handleSubmit} disabled={loading || !numbers.length}>
                    {loading ? t.common.loading : t.common.save}
                </button>
            </footer>
        </section>
    );
}
